// ANCHOR React
import { useEffect } from 'react';

// ANCHOR Models
import { useProviderCourseError } from '@lyon/scoped-models/ProviderCourseData';
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';
import { ProviderData } from '@lyon/scoped-models/ProviderData';

// ANCHOR Graph States
import { useAuthAdmin } from '@lyon/nodes/firebase/auth/auth-admin-node';

// ANCHOR Utils
import { convertToSlug } from '@lyon/utils/extras/convertToSlug';
import { track } from '@lyon/utils/segment';
import { ucarecdn } from '@lyon/utils/ucarecdn';

// ANCHOR Results
import { ICourseResult } from '@lyon/utils/api/results/course';

// ANCHOR Components
import { LyonCourseCardSkeleton } from '@lyon/components/course-card-revision/LyonCourseCardSkeleton';
import { LyonCourseCardRevision } from '@lyon/components/course-card-revision/LyonCourseCard';
import { LyonEmptyState } from '@lyon/components/empty/LyonEmptyState';
import { LyonContentCards } from '@lyon/components/content/LyonContentCards';

// ANCHOR Constants
import { EMPTY } from './constants';

interface IProps {
  data?: ICourseResult[];
}

export function LyonProviderCourses({ data }: IProps) {
  const isProvider = ProviderCheckManager.useSelector((state) => state.data);
  const isAdmin = useAuthAdmin();
  const error = useProviderCourseError();
  const id = ProviderData.useSelector((state) => state.data?.id);

  useEffect(() => {
    if (data && id) {
      const products = data.map((course, index) => ({
        productId: course.id,
        productPosition: index + 1,
        name: course.title,
        price: course.price,
        createdAt: course.createdAt,
        imageUrl: course.courseDisplayPhotoUuid
          ? ucarecdn(course.courseDisplayPhotoUuid)
          : undefined,
        url: `https://lyon.com.ph/course/${String(course.id)}/${convertToSlug(course.title)}`,
      }));

      track('Product List Viewed', {
        listId: id,
        products,
      }, isAdmin);
    }
  }, [data, id, isAdmin]);

  if (data) {
    if (data.length) {
      return (
        <>
          <LyonContentCards layout="column">
            {
              data.map((course) => (
                <LyonCourseCardRevision
                  key={course.id}
                  course={course}
                  fromProvider
                  isProviderManager={!!isProvider}
                />
              ))
            }
          </LyonContentCards>
        </>
      );
    }

    return <LyonEmptyState label={EMPTY} hasImage />;
  }

  if (error) {
    return <LyonEmptyState label={EMPTY} hasImage />;
  }

  return <LyonCourseCardSkeleton />;
}
